import { isUndefined } from 'lodash-es';
import { useContext, type MouseEvent as ReactMouseEvent } from 'react';

import { useAnnotationsSelectionState } from './store/selectors.js';
import {
  useSetStateSelection,
  useSetStateTooltip,
} from './store/state-setters.js';
import { useAnnotationsHandlers } from './useAnnotationsHandlers.js';
import { useMarkersInteractions } from './useMarkersInteractions.js';
import { TRACK_HEIGHT } from '../constants.js';
import { MarkerCoordinatesContext } from '../contexts/MarkerCoordinates.context.js';
import { initialState } from '../providers/AnnotationsDataInitialState.js';
import { inMarkerBounds } from '../utils/in-marker-bounds.js';

export const useOverlayEvents = () => {
  const markerCoordsPerTrack = useContext(MarkerCoordinatesContext);

  const {
    hoverTrack,
    blurTrack,
    hoverMarker,
    blurMarker,
    clickMarker,
    currentHoveredTrack,
    currentHoveredMarker,
  } = useAnnotationsHandlers();

  const { pinnedGroup } = useAnnotationsSelectionState();
  const updateTooltipState = useSetStateTooltip();
  const updateTooltipSelection = useSetStateSelection();
  const onMarkersInteraction = useMarkersInteractions();

  const handleMouseMove = (e: ReactMouseEvent) => {
    const { clientX, clientY, currentTarget } = e;
    const { top, left } = (currentTarget as Element).getBoundingClientRect();

    const mousePosition = {
      x: clientX - left,
      y: clientY - top,
    };

    let trackIndex = Math.max(0, Math.floor(mousePosition.y / TRACK_HEIGHT));

    if (trackIndex !== currentHoveredTrack.current) {
      hoverTrack(e, trackIndex);
    }

    if (markerCoordsPerTrack !== undefined) {
      let isHoveringMarker = false;

      if (trackIndex >= markerCoordsPerTrack.length) {
        trackIndex = markerCoordsPerTrack.length - 1;
      }

      const markerCoords = markerCoordsPerTrack[trackIndex];

      // start from the end of the array to look first for the markers that are rendered on top
      for (
        let markerIndex = markerCoords.length - 1;
        markerIndex >= 0;
        markerIndex--
      ) {
        const marker = markerCoords[markerIndex];

        if (inMarkerBounds(mousePosition, trackIndex, marker)) {
          const markerId = `${trackIndex}_${marker.join('_')}`;

          hoverMarker(e, markerId);

          isHoveringMarker = true;
          break;
        }
      }

      // no marker found at the given mouse position
      if (!isHoveringMarker && currentHoveredMarker.current !== undefined) {
        blurMarker(e, currentHoveredMarker.current);

        // hover the track back when leaving the marker
        hoverTrack(e, trackIndex);
      }
    }
  };

  const handleClick = (e: ReactMouseEvent) => {
    if (currentHoveredMarker.current !== undefined) {
      clickMarker(e, currentHoveredMarker.current);
      return;
    }

    if (!isUndefined(pinnedGroup)) {
      updateTooltipState(initialState.tooltip);
      updateTooltipSelection(initialState.selection);
      onMarkersInteraction?.(initialState.selection);
    }
  };

  const handleMouseLeave = (e: ReactMouseEvent) => {
    if (currentHoveredTrack.current !== undefined) {
      blurTrack();
    }

    if (currentHoveredMarker.current !== undefined) {
      blurMarker(e, currentHoveredMarker.current);
    }
  };

  return {
    handleMouseMove,
    handleClick,
    handleMouseLeave,
  };
};
